/* @flow */
import * as React from 'react';
import styled from 'styled-components';

import { Box, Flex, Icon, Space, Text, defaultTheme } from '@sonyged/proc-ui';
import { Button, ButtonIcon, Footer, ProcModal } from '@sonyged/ext-proc';
import css from '@styled-system/css';

import { ContentScroll } from '../../common/components/ContentScroll';
import type { Sushi } from '../rules/types';
import { SushiContext } from '../SushiContext';

type Props = {
  value: ?Sushi,
  onChange: Sushi => void,
  className?: string,
  blankLabel: string,
  moreKanji?: boolean,
};

export function Select(props: Props) {
  const { value, onChange, className = '', blankLabel, moreKanji, ...rest } = props;

  const mapper = React.useContext(SushiContext);

  const groups = React.useMemo(() => mapper.getSushiGroups(), [mapper]);

  const erabu = moreKanji ? '選ぶ' : 'えらぶ';

  const [isOpened, setOpen] = React.useState(false);

  function onClick() {
    setOpen(true);
  }

  function onClose() {
    setOpen(false);
  }

  return (
    <Container {...rest} onClick={isOpened ? null : onClick} className={'proc-btn ' + className}>
      <ValueText color={value ? 'gray100' : 'primary120'}>{value ? mapper.getSushiName(value) : blankLabel}</ValueText>

      <Space ml={['8px', '8px', '12px']} mr={['-4px', '-4px', '-6px']}>
        <Text>
          <Icon variant="dropdown" />
        </Text>
      </Space>

      <ProcModal isOpened={isOpened} rootNodeId="proc-sushi-fields-root-page">
        <div key="modal" className={'proc-modal proc-modal--hint'}>
          <div className="proc-modal-container">
            <div className={'proc-modal-header'}>{erabu}</div>
            <div className={'proc-modal-contents'}>
              <Contents>
                <ContentScroll flex={1} minHeight={0} gradientHeight={12}>
                  {groups.map((group, i) => (
                    <Space key={i}>
                      <Heading>
                        <HeadingIcon src={group.icon} alt="dish" />
                        <HeadingText>{group.name}</HeadingText>
                      </Heading>

                      <Flex flexWrap="wrap">
                        {group.sushi.map((sushi, j) => (
                          <Item
                            key={j}
                            className="proc-choice-se"
                            onClick={() => {
                              onChange(sushi);
                              setOpen(false);
                            }}
                          >
                            <SushiBox bg={group.color}>
                              <SushiIcon src={mapper.getSushiIcon(sushi)} />
                            </SushiBox>
                            <SushiName>{mapper.getSushiName(sushi)}</SushiName>
                          </Item>
                        ))}
                      </Flex>
                    </Space>
                  ))}
                </ContentScroll>
              </Contents>
            </div>
          </div>

          <Footer.nav
            shadow={false}
            navItem={
              <Button.dark className="modal-close-se" onClick={onClose}>
                <ButtonIcon.close />
              </Button.dark>
            }
          />
        </div>
      </ProcModal>
    </Container>
  );
}

const ValueText = styled(Text)(
  css({
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  })
);

const Container = styled(Flex)(
  css({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: ['mobile.small', 'smallPC.small', 'largePC.small'],
    height: [36, 36, 48],
    px: [18, 18, 24],
    cursor: 'pointer',
    bg: 'primary40',
    borderRadius: 9999,
    transition: 'background-color 0.2s ease-out',
    '&:hover': {
      bg: 'primary60',
    },
  })
);

const Contents = styled(Box)(
  css({
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    px: 16,
  })
);

const Heading = styled(Flex)(
  css({
    alignItems: 'center',
    mb: ['12px', '12px', '16px'],
  })
);

const HeadingIcon = styled('img')(
  css({
    display: 'block',
    width: ['30px', '30px', '40px'],
    height: ['30px', '30px', '40px'],
  })
);
HeadingIcon.defaultProps = { theme: defaultTheme };

const HeadingText = styled(Text)(
  css({
    color: '#fff',
    ml: '8px',
    fontSize: ['18px', '18px', '20px'],
  })
);

const Item = styled(Flex)(
  css({
    flexShrink: 0,
    flexDirection: 'column',
    width: ['72px', '72px', '136px'],
    '&:not(:last-child)': {
      mr: ['10px', '10px', '30px'],
    },
    mb: ['24px', '24px', '32px'],
  })
);

const SushiBox = styled(Flex)(
  css({
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: ['72px', '72px', '136px'],
    borderRadius: ['12px', '12px', '36px'],
  })
);

const SushiName = styled(Text)(
  css({
    color: 'white',
    textAlign: 'center',
    lineHeight: 1.2,
    mt: '6px',
  })
);

const SushiIcon = styled('img')(
  css({
    maxWidth: '100%',
  })
);
